import gql from 'fake-tag'
import { cache } from 'services'

import DefaultClient from './DefaultClient'

class IntegrationsClient extends DefaultClient {
    constructor() {
        super(cache.keys.INTEGRATIONS)
    }

    async getFeaturedSlugs() {
        const cacheKey = `FEATURED_SLUGS`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
            {
                integrationFeaturedIntegrations(id: "4ZxRSi5Z9Sq0uH1LXiRoaG") {
                    title
                    selectionsCollection {
                        items {
                            ... on IntegrationsTwistCustomIntegration {
                                slug
                            }
                            ... on IntegrationsTwistCustomIntegration {
                                slug
                            }
                        }
                    }
                }
            }
        `)
        const resourceLinks = data.integrationFeaturedIntegrations.selectionsCollection
        const featuredSlugs = resourceLinks.items.map((a: any) => a.slug)
        this.cache.set(cacheKey, featuredSlugs)

        return featuredSlugs
    }

    async getCategories(lang = 'en') {
        const locale = this.getLocale(lang)
        const cacheKey = `CATEGORIES_${locale}`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
            {
                integrationsCategoryCollection(locale: "${locale}") {
                    items {
                        name
                        slug
                        description
                    }
                }
            }
        `)
        const categories = data.integrationsCategoryCollection.items
        this.cache.set(cacheKey, categories)

        return categories
    }

    async getIntegrations(lang = 'en') {
        const customIntegrations = await this._getCustomIntegrations(lang)
        const zapierIntegrations = await this._getZapierIntegrations(lang)

        return [...customIntegrations, ...zapierIntegrations].sort((a, b) =>
            a.name.localeCompare(b.name),
        )
    }

    async _getCustomIntegrations(lang = 'en') {
        const locale = this.getLocale(lang)
        const cacheKey = `INTEGRATIONS_LIST_CUSTOM_${locale}`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
                {
                    integrationsTwistCustomIntegrationCollection(order: [title_ASC], locale: "${locale}") {
                        items {
                            slug
                            name: title
                            commonContent {
                                tagline
                                categoriesCollection {
                                    items {
                                        slug
                                        name
                                    }
                                }
                                appLogo {
                                    alt: title
                                    url
                                }
                                featuredScreenshot {
                                    title
                                    url
                                }
                            }
                        }
                    }
                }
        `)
        const integrations = data.integrationsTwistCustomIntegrationCollection.items
        integrations.forEach((integration: any) => addCategoryToIntegration(integration))
        this.cache.set(cacheKey, integrations)

        return integrations
    }

    async _getZapierIntegrations(lang = 'en') {
        const locale = this.getLocale(lang)
        const cacheKey = `INTEGRATIONS_LIST_ZAPIER_${locale}`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
            {
                integrationsTwistZapierIntegrationCollection(locale: "${locale}") {
                    items {
                        slug
                        name: title
                        installUrl: installButton
                        commonContent {
                            tagline
                            categoriesCollection {
                                items {
                                    slug
                                    name
                              }
                            }

                            appLogo {
                                alt: title
                                url
                            }
                            featuredScreenshot {
                                title
                                url
                            }
                        }
                    }
                }
            }
        `)
        if (!data) return []

        const integrations = data.integrationsTwistZapierIntegrationCollection.items
        integrations.forEach((integration: any) => addCategoryToIntegration(integration))
        integrations.forEach((integration: any) => (integration.zapier = true))
        this.cache.set(cacheKey, integrations)

        return integrations
    }

    async getCustomIntegrationBySlug(slug: string, lang = 'en') {
        const locale = this.getLocale(lang)
        const cacheKey = `INTEGRATION_CUSTOM_${slug}_${locale}`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
            {
                integrationsTwistCustomIntegrationCollection(
                    locale: "${locale}",
                    where: {
                        slug: "${slug}"
                    }
                ) {
                    items {
                        slug
                        name: title
                        features
                        helpCenterUrl
                        installUrl
                        commonContent {
                            tagline
                            learnMoreButton
                            appWebsiteLink
                            privacyPolicyLink
                            supportLink
                            supportedLanguages
                            categoriesCollection {
                                items {
                                    slug
                                    name
                                }
                            }
                            appLogo {
                                alt: title
                                url
                            }
                            screenshotsCollection {
                                items {
                                    title
                                    url
                                }
                            }
                            about {
                                json
                            }
                        }
                        howToInstall {
                            json
                        }
                    }
                }
            }
        `)

        if (!data) return null

        const integration = data.integrationsTwistCustomIntegrationCollection.items[0]
        if (integration) {
            addCategoryToIntegration(integration)
            this.cache.set(cacheKey, integration)
        }

        return integration
    }

    async getZapierIntegrationBySlug(slug: string, lang = 'en') {
        const locale = this.getLocale(lang)
        const cacheKey = `INTEGRATION_ZAPIER_${slug}_${locale}`
        const cache = this.cache.get(cacheKey)
        if (cache) return cache

        const data = await this.queryGraphQL(gql`
            {
                integrationsTwistZapierIntegrationCollection(
                    locale: "${locale}",
                    where: {
                        slug: "${slug}"
                    }
                ) {
                    items {
                        slug
                        name: title
                        features
                        installUrl: installButton
                        commonContent {
                            tagline
                            learnMoreButton
                            appWebsiteLink
                            privacyPolicyLink
                            supportLink
                            supportedLanguages
                            categoriesCollection {
                                items {
                                    slug
                                    name
                                }
                            }
                            appLogo {
                                alt: title
                                url
                            }
                            screenshotsCollection {
                                items {
                                    title
                                    url
                                }
                            }
                            about {
                                json
                            }
                        }
                    }
                }
            }
        `)

        if (!data) return null

        const integration = data.integrationsTwistZapierIntegrationCollection.items[0]
        if (typeof integration !== 'undefined') integration.zapier = true
        this.cache.set(cacheKey, integration)

        return integration
    }
}

function addCategoryToIntegration(integration: any) {
    const categories = integration.commonContent.categoriesCollection.items.filter(Boolean)
    if (categories.length > 0) {
        integration.commonContent.category = categories[0]
    } else {
        integration.commonContent.category = { name: 'Other', slug: 'other' }
    }
}

export default new IntegrationsClient()
